import { axiosInstance, queryClient } from "@/api/client";
import { TAXONOMY_KEY } from "@/api/query-keys";
import { isClientError } from "@/api/utils/error";
import {
  setTaxonomyStatus,
  setTaxonomyTree,
  taxonomyStatus,
} from "../store/taxonomy-store";
import type { TaxonomyResponse, TaxonomyTree } from "./types";

const TAXONOMY_PATH = "/v1/taxonomy";

/**
 * AC2/AC8: a 4xx cannot change by repeating the same request (the `404` production
 * answers until the backend release is on `main` included), and a retried 429 spends
 * another slot of the same bucket — so a client error is never retried. A 5xx or a
 * network blip can change, once. Same shape as the dashboard's. It has to be declared:
 * `fetchQuery` defaults `retry` to `false`, not to the three retries of `useQuery`.
 */
const retryUnlessClientError = (failureCount: number, error: unknown) =>
  !isClientError(error) && failureCount < 1;

// ============================================
// Taxonomy (GET /v1/taxonomy)
// ============================================

// 1. Base fetcher — unwraps the `{ success, data }` envelope, no error handling here.
export const getTaxonomy = async (): Promise<TaxonomyTree> => {
  const { data } = await axiosInstance.get<TaxonomyResponse>(TAXONOMY_PATH);
  return data.data;
};

// 2. Query config creator.
//
// The label store is written INSIDE the `queryFn`, not from the result of `fetchQuery`.
// `queryClient.clear()` (magic-link login) and `resetQueries()` (workspace switch)
// cancel a query that is in flight, so `fetchQuery` rejects with `CancelledError` — but
// this `queryFn` does not use `signal`, so it still runs to the end. Writing here keeps
// the labels for the session. They are not user data: they are never requested again
// after a clear (AC6). The status is written here too, for the same reason.
export const createTaxonomyQueryConfig = () => ({
  queryKey: [TAXONOMY_KEY],
  queryFn: async () => {
    const tree = await getTaxonomy();
    setTaxonomyTree(tree);
    setTaxonomyStatus("ready");
    return tree;
  },
  staleTime: Number.POSITIVE_INFINITY,
  retry: retryUnlessClientError,
});

// 3. Imperative loader, in place of a hook. A `useQuery` per consumer would suspend the
// nearest `<Suspense>` on first read (see the store); the tree is fetched once at
// startup and read from the store. Never throws and shows nothing — a slow or failed
// request leaves the keys on screen (AC2, AC8).
//
// Status (AC15):
// - `loading` is set before `fetchQuery`, unless the tree is already there: a cached
//   answer does not run the `queryFn`, so a second call would otherwise leave `loading`
//   with nothing to end it.
// - `failed` is set only by the catch, and only while the status is still `loading`.
//   A `clear()` or `resetQueries()` while the request is in flight rejects `fetchQuery`
//   with `CancelledError`, but the `queryFn` still stores the tree and sets `ready`.
//   If that happened already, the catch must not overwrite it. (If the cancel comes
//   while the request is still pending, `failed` shows for the moment until the answer
//   arrives and sets `ready`.)
export const loadTaxonomy = async (): Promise<void> => {
  if (taxonomyStatus() !== "ready") setTaxonomyStatus("loading");
  try {
    await queryClient.fetchQuery(createTaxonomyQueryConfig());
  } catch {
    if (taxonomyStatus() === "loading") setTaxonomyStatus("failed");
  }
};

/** Loads the tree again after `failed` (AC15). */
export const retryTaxonomy = (): Promise<void> => loadTaxonomy();
